import { z } from 'zod';
import { TRADE_LIMITS } from '../constants/index.js';

// --- Enums ---

export const tradeEventSourceSchema = z.enum([
  'mt5',
  'ctrader',
  'simulator',
  'tradingview',
  'manual',
]);

export const tradeEventTypeSchema = z.enum([
  'DEAL_EXECUTED',
  'ORDER_PLACED',
  'ORDER_MODIFIED',
  'ORDER_CANCELLED',
  'POSITION_CLOSED',
]);

export const tradeEventSideSchema = z.enum(['BUY', 'SELL']);

// --- Create schema (POST body) ---

export const tradeEventCreateSchema = z.object({
  source: tradeEventSourceSchema,
  eventType: tradeEventTypeSchema,
  symbol: z.string().min(1).max(30),
  side: tradeEventSideSchema,
  volume: z.number().positive().max(TRADE_LIMITS.QUANTITY_MAX),
  price: z.number().positive().max(TRADE_LIMITS.PRICE_MAX),
  stopLoss: z.number().positive().max(TRADE_LIMITS.PRICE_MAX).optional().nullable(),
  takeProfit: z.number().positive().max(TRADE_LIMITS.PRICE_MAX).optional().nullable(),
  profit: z.number().min(-TRADE_LIMITS.PRICE_MAX).max(TRADE_LIMITS.PRICE_MAX).optional().nullable(),
  commission: z.number().min(-TRADE_LIMITS.FEES_MAX).max(TRADE_LIMITS.FEES_MAX).optional().nullable(),
  swap: z.number().min(-TRADE_LIMITS.FEES_MAX).max(TRADE_LIMITS.FEES_MAX).optional().nullable(),
  accountId: z.string().max(50).optional().nullable(),
  orderId: z.string().max(50).optional().nullable(),
  positionId: z.string().max(50).optional().nullable(),
  platformEventId: z.string().min(1).max(100).optional().nullable(),
  executedAt: z.string().datetime(),
  comment: z.string().max(500).optional().nullable(),
  platformData: z.record(z.unknown()).optional().nullable(),
});

// --- Redis pub/sub schema (full persisted event published after save) ---
// The service publishes the Prisma TradeEvent record, which includes id, userId,
// and server-set timestamps alongside the create fields.
//
// Design notes:
// - `executedAt` / `createdAt` use bare z.string() because Prisma serialises DateTime
//   as ISO 8601 and z.string().datetime() offset-strictness rules differ across Zod
//   minor versions — the bare form is safer here.
// - `rawPayload` and `platformData` are intentionally excluded: both are internal/opaque
//   fields with no meaning for WS subscribers, and no frontend consumer reads them.
// - Zod z.object() strips unknown keys by default, so future DB columns won't
//   silently reach WS clients — the schema must be updated explicitly to forward them.

export const tradeEventRedisSchema = z.object({
  id: z.string().uuid(),
  userId: z.string(),
  source: z.string().min(1),
  eventType: tradeEventTypeSchema,
  symbol: z.string().min(1).max(30),
  side: tradeEventSideSchema,
  volume: z.number(),
  price: z.number(),
  stopLoss: z.number().nullable().optional(),
  takeProfit: z.number().nullable().optional(),
  profit: z.number().nullable().optional(),
  commission: z.number().nullable().optional(),
  swap: z.number().nullable().optional(),
  accountId: z.string().nullable().optional(),
  orderId: z.string().nullable().optional(),
  positionId: z.string().nullable().optional(),
  platformEventId: z.string().nullable().optional(),
  executedAt: z.string(),
  comment: z.string().nullable().optional(),
  createdAt: z.string(),
});

export type TradeEventRedis = z.infer<typeof tradeEventRedisSchema>;

// --- Filters schema (GET query params) ---

export const tradeEventFiltersSchema = z.object({
  source: z.string().min(1).max(100).optional(),
  eventType: tradeEventTypeSchema.optional(),
  symbol: z.string().optional(),
  side: tradeEventSideSchema.optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});
